import { inject, Inject, Injectable } from '@angular/core';
import { Lookup, LookupKeys, Lookups } from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { doc, DocumentSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CommonFireStoreDao, QueryParam } from '../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../injections/firebase-app';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { SortDescriptor } from 'devextreme/data';
import {
  BatchCommand,
  BatchCommandKeys,
  BatchCommandType,
  BatchOperationsService,
} from 'ag-common-svc/lib/dao/batch-firestore.service';

@Injectable({ providedIn: 'root' })
export class LookupsManagerService {
  public readonly tenantId: string;
  public readonly fsDao: CommonFireStoreDao<Lookup>;
  public readonly collection = 'lookups';
  readonly tenantSortingId = 'default-sorting';

  private batchOperationsService = inject(BatchOperationsService);

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    this.fsDao = new CommonFireStoreDao<Lookup>(fireBaseApp, LookupsManagerService.fromFirestore);
    this.tenantId = 'default'; // TODO get tenant id form auth
  }

  public getLookup = (lookupCategory: Lookups, lookupId: string): Observable<DocumentSnapshot<Lookup>> => {
    const path = this.getPath(lookupCategory);

    return this.fsDao.getDocument(path, lookupId);
  };

  public getList = (lookupId: Lookups, queryParams: QueryParam[] = [], includeRef: boolean = true): Observable<any> => {
    const path = this.getPath(lookupId);

    return this.fsDao.getList(path, queryParams, { includeRef }, false);
  };

  public create = async (lookupId: Lookups, lookup: Lookup, lookupItems$?: Observable<Lookup[]>) => {
    const path = this.getPath(lookupId);

    if (lookup?.isDefault) {
      await this.uncheckDefault(lookupId, lookupItems$);
    }

    return this.fsDao.create(lookup, path);
  };

  public createWithId = async (lookupId: Lookups, lookup: Lookup, lookupItems$?: Observable<Lookup[]>) => {
    const path = this.getPath(lookupId);
    if (lookup?.isDefault) {
      await this.uncheckDefault(lookupId, lookupItems$);
    }
    const uid = lookupId === Lookups.MediaTags ? lookup[BaseModelKeys.dbId] : lookup[LookupKeys.value];
    return this.fsDao.createWithId(lookup, uid, path);
  };

  public update = async (
    lookupId: Lookups,
    documentId: string,
    updates: Partial<Lookup>,
    lookupItems$?: Observable<Lookup[]>,
  ) => {
    const path = this.getPath(lookupId);
    if (updates?.isDefault) {
      await this.uncheckDefault(lookupId, lookupItems$);
    }
    return this.fsDao.updateFields(updates, documentId, path);
  };

  public delete = (lookupId: Lookups, documentId: string) => {
    const path = this.getPath(lookupId);

    return this.fsDao.delete(documentId, path);
  };

  updateOrder = async (
    lookupId: Lookups | string,
    orderedItems: Array<{ dbId: string; orderBy: number }>,
    sortDescriptor: SortDescriptor<string>[],
  ): Promise<void> => {
    // Extract the base lookup enum from composite ID (e.g., "task-subcategory.categoryId" -> Lookups.TaskSubcategory)
    const baseLookupId = typeof lookupId === 'string' && lookupId.includes('.')
      ? (lookupId.split('.')[0] as Lookups)
      : (lookupId as Lookups);
    const path = this.getPath(baseLookupId);
    const items = Array.isArray(orderedItems) ? orderedItems.filter(x => !!x?.dbId) : [];

    if (!items.length) {
      return;
    }

    const commands: BatchCommand[] = items.map(item => ({
      [BatchCommandKeys.type]: BatchCommandType.update,
      [BatchCommandKeys.dbId]: item.dbId,
      [BatchCommandKeys.table]: path,
      [BatchCommandKeys.data]: {
        [LookupKeys.orderBy]: item.orderBy,
      },
    }));
    await this.batchOperationsService.runBatchOperations(commands);

    // Save sorting only after all batches committed
    await this.updateDefaultSorting(lookupId, sortDescriptor);
  };

  async updateDefaultSorting(lookupId: Lookups | string, sortDescriptor: SortDescriptor<string>[]): Promise<void> {
    if (!lookupId) {
      return;
    }

    const path = this.getSortingPath();
    const sortingRef = doc(this.fsDao.db, path);

    await setDoc(
      sortingRef,
      {
        [lookupId]: Array.isArray(sortDescriptor) ? sortDescriptor : [],
      },
      { merge: true },
    );
  }

  async getDefaultSorting(lookupId: Lookups | string): Promise<SortDescriptor<string>[]> {
    if (!lookupId) {
      return [];
    }

    const path = this.getSortingPath();
    const sortingRef = doc(this.fsDao.db, path);

    try {
      const snap = await getDoc(sortingRef);

      if (!snap.exists()) {
        return [];
      }

      const data: any = snap.data();
      const stored = Array.isArray(data?.[lookupId]) ? data[lookupId] : [];

      return stored as SortDescriptor<string>[];
    } catch (error) {
      // Swallow Firestore permission and other read errors for default sorting,
      // so UI lookups (e.g. genders) still work with their own fallback logic.
      console.warn(`Failed to load default sorting for lookup "${lookupId}":`, error);
      return [];
    }
  }

  private uncheckDefault = async (lookupId: Lookups, lookupItems$?: Observable<Lookup[]>) => {
    const items = await lookupItems$.pipe(take(1)).toPromise();

    if (!Array.isArray(items) || !items?.length) {
      return null;
    }

    return items
      .filter(item => item?.isDefault)
      .map(lookup => {
        const path = this.getPath(lookupId);

        return this.fsDao.updateFields({ [LookupKeys.isDefault]: false }, lookup[BaseModelKeys.dbId], path);
      });
  };

  private getPath = (lookupId: Lookups): string => {
    return [this.collection, this.tenantId, lookupId].join('/');
  };

  private getSortingPath(): string {
    return [this.collection, this.tenantSortingId].join('/');
  }

  static readonly fromFirestore = (data): Lookup => {
    return Object.assign({}, data, {
      [LookupKeys.isDefault]: data[LookupKeys.isDefault] ?? false,
    });
  };
}
